import React, { type FC, useEffect } from "react";
import { connect, type ConnectedProps } from "react-redux";
import Restricted from "features/Application/Restricted";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import ContentGroupsNoResults from "features/Library/Common/Fields/ContentGroupsNoResults";
import { SearchInput } from "components";
import { AssignmentPeopleContext, RolePermissions } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { getGroupColumnOptions } from "features/Library/PeopleAssignments/Overview/helpers/getColumnOptions";
import { useContentAssignmentsPermissions } from "hooks/useContentAssignmentsPermissions";
import { fetchExternalLinkGroups } from "../state/thunks/externalLinkQueriesThunk";

interface GroupsOverviewOwnProps {
  linkId: number;
  customHeaderContent: React.ReactElement;
}

type Props = GroupsOverviewOwnProps & PropsFromRedux;

const GroupsOverview: FC<Props> = ({ linkId, customHeaderContent, groups, fetchExternalLinkGroups: fetchGroups }) => {
  const permissionPredicate = useContentAssignmentsPermissions(
    AssignmentPeopleContext.Group,
    RolePermissions.AssetsManage,
  );

  useEffect(() => {
    fetchGroups(linkId);
  }, [fetchGroups, linkId]);

  return (
    <Restricted
      permissions={[]}
      permissionPredicate={permissionPredicate}
      renderContent={() => (
        <GenericItemsView
          items={groups.items}
          isLoading={groups.isLoading}
          dataCount={groups.count}
          columnOptions={getGroupColumnOptions()}
          renderSearch={() => <SearchInput placeholder="Search for Groups..." disabled />}
          customHeaderContent={customHeaderContent}
          noResultsContent={<ContentGroupsNoResults />}
        />
      )}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  groups: state.library.externalLinks.details.groups,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchExternalLinkGroups: bindAction(fetchExternalLinkGroups, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedGroupsOverview = connector(GroupsOverview);

export default ConnectedGroupsOverview;
